import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, ArrowLeft, Circle, Square, RectangleHorizontal, ArrowRight } from 'lucide-react';

const badgeOptions = [
  {
    id: 'round-44',
    type: 'round',
    label: '44mm Round',
    icon: Circle,
    designArea: 54,
    badgeSize: 44,
    description: 'Badge: 44mm | Print area: 54mm',
    popular: true
  },
  {
    id: 'round-58',
    type: 'round',
    label: '58mm Round',
    icon: Circle,
    designArea: 70,
    badgeSize: 58,
    description: 'Badge: 58mm | Print area: 70mm',
    popular: true
  },
  {
    id: 'square-50',
    type: 'square',
    label: '50×50mm Square',
    icon: Square,
    designArea: 62,
    badgeSize: 50,
    description: 'Badge: 50×50mm | Print area: 62×62mm',
    popular: false
  },
  {
    id: 'rect-80-53',
    type: 'rectangle',
    label: '80×53mm Rectangle',
    icon: RectangleHorizontal,
    designWidth: 95,
    designHeight: 70,
    badgeWidth: 80,
    badgeHeight: 53,
    description: 'Badge: 80×53mm | Print area: 95×70mm',
    popular: true
  },
  {
    id: 'rect-90-65',
    type: 'rectangle',
    label: '90×65mm Rectangle',
    icon: RectangleHorizontal,
    designWidth: 109,
    designHeight: 85,
    badgeWidth: 90,
    badgeHeight: 65,
    description: 'Badge: 90×65mm | Print area: 109×85mm',
    popular: false
  }
];

export default function BadgeSizeSelection() {
  const navigate = useNavigate();
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [orientation, setOrientation] = useState('portrait');

  const handleCreate = () => {
    if (!selectedBadge) return;
    
    // Navigate to editor with serializable badge data only (no React components)
    const badgeData = {
      id: selectedBadge.id,
      type: selectedBadge.type,
      label: selectedBadge.label,
      designArea: selectedBadge.designArea,
      badgeSize: selectedBadge.badgeSize,
      designWidth: selectedBadge.designWidth,
      designHeight: selectedBadge.designHeight,
      badgeWidth: selectedBadge.badgeWidth,
      badgeHeight: selectedBadge.badgeHeight,
      description: selectedBadge.description
    };
    
    navigate('/editor', {
      state: {
        badge: badgeData,
        orientation: selectedBadge.type === 'rectangle' ? orientation : null
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-playful">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold gradient-text">BadgeMaker Studio</h1>
                <p className="text-xs text-muted-foreground">Professional Badge Editor</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Title */}
          <div className="text-center space-y-3">
            <h2 className="text-4xl font-bold">Choose Your Badge Size</h2>
            <p className="text-lg text-muted-foreground">Select the perfect size for your badges</p>
          </div>

          {/* Badge Size Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {badgeOptions.map((badge) => {
              const Icon = badge.icon;
              const isSelected = selectedBadge?.id === badge.id;
              
              return (
                <Card
                  key={badge.id}
                  onClick={() => setSelectedBadge(badge)}
                  className={`card-playful p-6 cursor-pointer transition-all duration-300 relative overflow-hidden ${
                    isSelected
                      ? 'border-2 border-primary bg-primary/5 shadow-playful scale-105'
                      : 'border border-border hover:border-primary/50 hover:scale-102'
                  }`}
                >
                  {badge.popular && (
                    <Badge className="absolute top-3 right-3 bg-gradient-to-r from-primary to-secondary text-primary-foreground">
                      Popular
                    </Badge>
                  )}
                  
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center transition-transform duration-300 ${
                      isSelected ? 'scale-110' : ''
                    }`}>
                      <Icon className="w-10 h-10 text-primary" />
                    </div>
                    
                    <div>
                      <h3 className="font-bold text-lg mb-1">{badge.label}</h3>
                      <p className="text-sm text-muted-foreground">{badge.description}</p>
                    </div>
                    
                    {isSelected && (
                      <div className="w-full pt-2">
                        <Badge className="w-full justify-center bg-success text-success-foreground">
                          Selected
                        </Badge>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Orientation Selection for Rectangle */}
          {selectedBadge?.type === 'rectangle' && (
            <Card className="card-playful p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <h3 className="font-semibold mb-4 text-center">Paper Orientation</h3>
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <Button
                  variant={orientation === 'portrait' ? 'default' : 'outline'}
                  onClick={() => setOrientation('portrait')}
                  className="rounded-xl h-auto py-4 flex flex-col gap-2"
                >
                  <RectangleHorizontal className="w-6 h-6 rotate-90" />
                  <span>Portrait</span>
                </Button>
                <Button
                  variant={orientation === 'landscape' ? 'default' : 'outline'}
                  onClick={() => setOrientation('landscape')}
                  className="rounded-xl h-auto py-4 flex flex-col gap-2"
                >
                  <RectangleHorizontal className="w-6 h-6" />
                  <span>Landscape</span>
                </Button>
              </div>
            </Card>
          )}

          {/* Create Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleCreate}
              disabled={!selectedBadge}
              size="lg"
              className="rounded-2xl text-lg px-12 py-7 bg-gradient-to-r from-primary to-secondary hover:shadow-hover transition-all duration-300 hover:scale-105"
            >
              Create Badge Design
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
